"use client";

import StoriesCard from "@/components/molecules/StoriesCard";
import Carousel6 from "@/components/organisms/carousels/Carousel6";
import Carousel7 from "@/components/organisms/carousels/Carousel7";
import {
  cardClick,
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  navigationClick,
  videoAction,
} from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { useCallback, useMemo, useRef, useState } from "react";

interface StoryMetadata {
  id: string;
  image: string;
  alt_image?: string;
  priority: number;
  name_text: string;
  video_link: string;
  designation: string;
  name_text_color: string;
  gradient_end_color: string;
  gradient_start_color: string;
  designation_text_color: string;
  gradient_angle?: string;
  gradient_start_position?: string;
  gradient_end_position?: string;
}

interface SuccessStoriesData {
  title?: string;
  title_text_color?: string;
  background_color?: string;
  left_navigation_icon?: string;
  left_navigation_icon_alt?: string;
  right_navigation_icon?: string;
  right_navigation_icon_alt?: string;
  metadata?: StoryMetadata[];
}

interface SuccessStories1Props {
  data?: {
    data?: SuccessStoriesData;
  };
}

const SuccessStories1 = ({ data }: SuccessStories1Props) => {
  const isMobile = useIsMobile(800);
  const [activeVideoIndex, setActiveVideoIndex] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const {
    title = "Success Stories",
    title_text_color = "#000000",
    background_color = "#FFFFFF",
    left_navigation_icon,
    left_navigation_icon_alt = "Previous",
    right_navigation_icon,
    right_navigation_icon_alt = "Next",
    metadata = [],
  } = data?.data || {};

  // Sort stories by priority
  const stories = useMemo(() => {
    return [...metadata].sort((a, b) => (a.priority || 0) - (b.priority || 0));
  }, [metadata]);

  if (stories.length === 0) {
    return null;
  }

  // Only show navigation when more than 4 cards in desktop
  const showNavigation = stories.length > 4;

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // Track widget viewed
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "success_stories_section");
  //   }
  // }, []);

  // Handle navigation click with tracking
  const handleNavigationClick = useCallback(
    (direction: "prev" | "next", currentIndex: number) => {
      navigationClick({
        section: "success_stories_section",
        component: "carousel_navigation",
        label: direction === "prev" ? "previous" : "next",
        index: String(currentIndex),
      });
    },
    []
  );

  // Handle card click with tracking
  const handleCardClick = useCallback(
    (story: StoryMetadata, index: number) => {
      cardClick({
        section: "success_stories_section",
        component: "success_story_card",
        label: story.name_text || `Story ${index + 1}`,
        parent: title,
        redirectLink: story.video_link || "",
        pageKey: "",
      });
    },
    [title]
  );

  // Handle video play with tracking
  const handleVideoPlay = useCallback((story: StoryMetadata, index: number) => {
    videoAction({
      section: "success_stories_section",
      label: story.video_link || story.name_text || `Story ${index + 1}`,
    });
    setActiveVideoIndex(index);
  }, []);

  return (
    <div
      ref={ref}
      className={cn(
        "flex flex-col items-center justify-center w-full",
        "xs:py-[42px] xs:gap-6",
        "sm:gap-6 sm:py-[42px]"
      )}
      style={{
        backgroundColor: background_color,
      }}
    >
      <div
        className={cn(
          "xs:text-heading-5-semibold",
          "sm:text-heading-5-semibold",
          "md:text-heading-4-semibold",
          "lg:text-new-heading-34-semibold",
          "xl:text-new-heading-42-semibold",
          "line-clamp-1"
        )}
        style={{
          color: title_text_color,
        }}
      >
        {title}
      </div>
      {isMobile ? (
        <div className="flex items-center justify-center w-full">
          <Carousel7
            left_navigation_icon={left_navigation_icon}
            right_navigation_icon={right_navigation_icon}
            startIndex={0}
            onNavigationClick={handleNavigationClick}
          >
            {stories.map((story: StoryMetadata, i: number) => (
              <StoriesCard
                key={story.id}
                index={i}
                isActive={activeVideoIndex === i}
                onPlay={() => {
                  handleCardClick(story, i);
                  handleVideoPlay(story, i);
                }}
                onClose={() => setActiveVideoIndex(null)}
                data={story}
              />
            ))}
          </Carousel7>
        </div>
      ) : (
        <div
          className={cn(
            "w-full",
            "sm:w-[1008px]",
            "md:w-[1070px]",
            "lg:w-[1320px]",
            "xl:w-[1594px]"
          )}
        >
          <Carousel6
            left_navigation_icon={
              showNavigation ? left_navigation_icon : undefined
            }
            left_navigation_icon_alt={
              showNavigation ? left_navigation_icon_alt : undefined
            }
            right_navigation_icon={
              showNavigation ? right_navigation_icon : undefined
            }
            right_navigation_icon_alt={
              showNavigation ? right_navigation_icon_alt : undefined
            }
            hideNavigation={!showNavigation}
            startIndex={0}
            gradientColor={background_color}
            onNavigationClick={handleNavigationClick}
          >
            {stories.map((story: StoryMetadata, i: number) => (
              <StoriesCard
                key={story.id}
                index={i}
                isActive={activeVideoIndex === i}
                onPlay={() => {
                  handleCardClick(story, i);
                  handleVideoPlay(story, i);
                }}
                onClose={() => setActiveVideoIndex(null)}
                data={story}
              />
            ))}
          </Carousel6>
        </div>
      )}
    </div>
  );
};

export default SuccessStories1;
